import { useState, useEffect } from 'react';
import { Translate } from 'react-redux-i18n';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPlus } from '@fortawesome/free-solid-svg-icons';
import { getViewEditSearchUrl } from '@helpers/getDashboardRedirectUrl';
import './CompanyDropdown.scss';
import get from 'lodash/get';
import useCountryCodeInfo from '@helpers/customHooks/useCountryCodeInfo';
import { CompanyDetails } from '@customTypes/ItemTypes';
import { CompanyDropdownProps } from '@customTypes/PropTypes';
import { sendGA4ClickEvent } from '@helpers/gtmUtils';

const CompanyDropdown = ({ companyData, activeCompany, switchCompany }: CompanyDropdownProps): JSX.Element => {
    const { featuresList } = useCountryCodeInfo();
    const { isAddCompanyCTAEnabled, addCompanyCTAURL } = featuresList;
    const [dropDownOptionFlag, setDropDownOptionFlag] = useState(false);
    const [companyName, setCompanyName] = useState(activeCompany.summary.businessName);
    const [activeDuns, setActiveDuns] = useState(activeCompany.profileDetails.dunsNumber);

    useEffect(() => {
        const closeDropDown = () => {
            setDropDownOptionFlag(false);
        };
        window.addEventListener('click', closeDropDown, false);
        return () => {
            window.removeEventListener('click', closeDropDown, false);
        };
    }, []);

    useEffect(() => {
        setCompanyName(activeCompany.summary.businessName);
        setActiveDuns(activeCompany.profileDetails.dunsNumber);
    }, [activeCompany]);

    const options = companyData.map((company: CompanyDetails, index: number) => {
        const mapDetails = company.mapDetails;
        let address = (mapDetails.buildingName != null) ? `${mapDetails.buildingName},` : '';
        address += (mapDetails.streetAddress != null) ? ` ${mapDetails.streetAddress}` : '';
        address += (mapDetails.town != null) ? `, ${mapDetails.town}` : '';
        address += (mapDetails.territory != null) ? `, ${mapDetails.territory}` : '';

        const activeCompanyName = get(activeCompany, 'summary.businessName', '');
        const companyName = get(company, 'summary.businessName', ' ');
        const dunsNumber = get(company, 'profileDetails.dunsNumber', '--');
        address = dunsNumber ? address : '';
        const fullAddress = (address.trim() != '') ? address : '  ';

        return (
            <div
                data-testid="select-option"
                className={`dropdown-item ${(activeCompanyName == companyName && activeDuns === dunsNumber) ? 'active' : ''}`}
                onClick={(event) => {
                    event.stopPropagation();
                    switchCompany(index);
                    setDropDownOptionFlag(false);
                    sendGA4ClickEvent('linkTrackingEvent', { linkTrackingName: 'changeCompanyDropdownClick' });
                }}
                key={dunsNumber}
            >
                <span className="dropdown-active">
                    {(activeCompanyName == companyName && activeDuns === dunsNumber)
                    && <FontAwesomeIcon icon={faEye} />}
                </span>
                <span className="company" title={companyName}>{companyName}</span>
                <span className="details">
                    <div className="address" title="address">{fullAddress}</div>
                </span>

            </div>
        );
    });
    return (
        <div
            className="company-dropdown"
            data-testid="company-dropdown"
        >
            <button
                type="button"
                className="flex items-center justify-between gap-2 h-11 bg-[#f6f7f8] rounded-lg flex-none m-0 text-[#222] border-0 p-2.5 max-w-56 text-base btn btn-primary after:inline-block align-[0.255em] after:[content: ''] after:border-t-[0.3em] after:border-r-transparent after:border-r-[0.3em] after:border-b-0 after:border-l-transparent after:border-l-[0.3em] after:box-border hover:border-grayish-blue-font"
                onClick={
                    (event) => {
                        event.stopPropagation();
                        setDropDownOptionFlag(!dropDownOptionFlag);
                    }
                }
            >
                <div className="font-avenirMedium mr-[5px] not-italic text-base font-medium leading-7 overflow-hidden text-ellipsis whitespace-nowrap text-[#000] desktop:font-[550] desktop:leading-9">
                    {companyName}
                </div>
            </button>
            {dropDownOptionFlag && (
                <div className="dropdown-menu" id="dropdown-menu">
                    <div className="dropdown-menu-scroll">
                        {options}
                    </div>
                    {isAddCompanyCTAEnabled && (
                        <div
                            className="bg-transparent border-launch-text border m-2.5 rounded-[7px] p-[5px] text-launch-text not-italic font-[550] text-base clear-both text-inherit whitespace-nowrap flex cursor-pointer flex-col justify-between"
                            key="addCompany"
                            onClick={(event) => {
                                event.stopPropagation();
                                setDropDownOptionFlag(false);
                                setTimeout(() => { window.open('/logout', '_self'); }, 1000);
                            }}
                        >
                            <a
                                href={getViewEditSearchUrl(addCompanyCTAURL)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => sendGA4ClickEvent('linkTrackingEvent', { linkTrackingName: 'addCompanyClick' })}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                <Translate value="add-a-company" />
                            </a>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CompanyDropdown;
